import React, { useEffect, useRef, useState } from "react";
import { useEditor, useEditorState, EditorContent, Extension, Mark } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { Table, TableRow, TableHeader, TableCell, TableView } from "@tiptap/extension-table";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { TableMap, columnResizingPluginKey } from "@tiptap/pm/tables";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle, FontSize, FontFamily, BackgroundColor } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import styled from "styled-components";
import { convertWordDocument } from "./wordImport";
import {
  narrativeBoxStyles,
  narrativeContentStyles,
  NARRATIVE_FONT_SIZE,
  NARRATIVE_LIST_COUNTER,
  TABLE_ALIGN_MARGINS,
  CELL_BORDER_COLOR,
  CELL_BORDER_WIDTH,
  MAX_CELL_BORDER_WIDTH,
  NARRATIVE_PADDING_X,
  NARRATIVE_PADDING_Y,
} from "./narrativeStyles";

const FONT_SIZES = [10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 40];

/**
 * 툴바 글꼴 목록. 값은 CSS font-family 문자열 그대로 들어간다.
 *
 * 내러티브는 기관(사이트) 브라우저에 그대로 나가는데 이 앱에는 웹폰트가 없다. 즉 여기 글꼴은
 * 전부 "보는 사람 PC에 깔려 있어야" 그대로 보인다. 그래서 OS 기본 글꼴 위주로만 두고,
 * 목록마다 영문 이름·한글 이름·제네릭 순으로 적는다 — 앞의 둘이 다 없어도 엉뚱한 글꼴이
 * 아니라 같은 계열(고딕/명조)로 떨어진다.
 *
 * 나눔 계열은 OS 기본은 아니지만 국내 기관 PC에는 사실상 깔려 있어 넣는다.
 *
 * Word에서 가져온 문서는 라틴/한글 글꼴이 함께 적혀 오므로(예: 'Calibri', '맑은 고딕')
 * 여기 없는 값도 그대로 살려 둔다 — fontFamilyOptions가 활성 값을 목록에 얹는다.
 */
export const FONT_FAMILIES = [
  ["맑은 고딕", "'Malgun Gothic', '맑은 고딕', sans-serif"],
  ["나눔고딕", "'NanumGothic', '나눔고딕', sans-serif"],
  ["돋움", "'Dotum', '돋움', sans-serif"],
  ["굴림", "'Gulim', '굴림', sans-serif"],
  ["바탕", "'Batang', '바탕', serif"],
  ["나눔명조", "'NanumMyeongjo', '나눔명조', serif"],
  ["Arial", "Arial, sans-serif"],
  ["Calibri", "Calibri, sans-serif"],
  ["Verdana", "Verdana, sans-serif"],
  ["Georgia", "Georgia, serif"],
  ["Times New Roman", "'Times New Roman', serif"],
  ["Courier New", "'Courier New', monospace"],
];

// 글꼴 이름만 뽑아 짧게 보여 준다 ("'Calibri', '맑은 고딕'" -> "Calibri, 맑은 고딕")
const fontLabel = (value) =>
  value
    .split(",")
    .map((name) => name.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean)
    .join(", ");

const INDENT_STEP = 24;
const MAX_INDENT = 8;

// margin-left 기반 들여쓰기. 단계를 만들 수 있는 목록은 sink/liftListItem이 먼저 맡고,
// 항목 자체의 여백은 Word에서 가져온 들여쓰기(글머리 기호까지 함께 밀리는 자리)를 지킨다.
export const Indent = Extension.create({
  name: "indent",

  addOptions() {
    return { types: ["paragraph", "heading", "listItem"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const ml = parseInt(el.style.marginLeft, 10);
              if (!ml || ml < 0) return 0;
              return Math.min(Math.round(ml / INDENT_STEP), MAX_INDENT);
            },
            renderHTML: (attrs) =>
              attrs.indent ? { style: `margin-left: ${attrs.indent * INDENT_STEP}px` } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    const shift = (dir) => () => ({ state, tr, dispatch }) => {
      const { from, to } = state.selection;
      let changed = false;

      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!this.options.types.includes(node.type.name)) return true;

        const current = node.attrs.indent || 0;
        const next = Math.min(Math.max(current + dir, 0), MAX_INDENT);
        if (next !== current) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
          changed = true;
        }

        // 목록 항목을 옮겼으면 그 안의 문단까지 또 옮기지 않는다 (두 번 들여쓰기가 된다)
        return false;
      });

      if (changed && dispatch) dispatch(tr);
      return changed;
    };

    return { indent: shift(1), outdent: shift(-1) };
  },

  addKeyboardShortcuts() {
    // 표 안에서는 Tab이 다음 셀 이동이므로 Table 확장에 넘긴다
    const inTable = () => this.editor.isActive("table");

    return {
      Tab: () =>
        !inTable() &&
        (this.editor.commands.sinkListItem("listItem") ||
          this.editor.commands.sinkListItem("taskItem") ||
          this.editor.commands.indent()),
      "Shift-Tab": () =>
        !inTable() &&
        (this.editor.commands.liftListItem("listItem") ||
          this.editor.commands.liftListItem("taskItem") ||
          this.editor.commands.outdent()),
    };
  },
});

const TEXT_ALIGN_VALUES = ["left", "center", "right", "justify"];

const normalizeTextAlign = (value) => {
  const normalized = (value || "").trim().toLowerCase();
  return TEXT_ALIGN_VALUES.includes(normalized) ? normalized : null;
};

// 문단·제목의 좌우 정렬. 표 셀 정렬(extension-table의 align)과 마찬가지로 인라인
// style로 저장되므로, 뷰어(NarrativeView·SiteNarrativeViewer)는 별도 규칙 없이
// 저장된 HTML 그대로 같은 모습을 낸다.
//
// "왼쪽"도 값을 비우지 않고 그대로 저장한다. 가운데 정렬한 셀 안에서 특정 문단만
// 왼쪽으로 되돌리려면 상속을 끊을 실제 값이 필요하기 때문이다.
const TextAlign = Extension.create({
  name: "textAlign",

  addOptions() {
    return { types: ["paragraph", "heading"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: null,
            // 외부에서 붙여 넣은 HTML은 style 대신 align 속성을 쓰기도 한다
            parseHTML: (el) => normalizeTextAlign(el.style.textAlign || el.getAttribute("align")),
            renderHTML: (attrs) =>
              attrs.textAlign ? { style: `text-align: ${attrs.textAlign}` } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextAlign: (align) => ({ state, tr, dispatch }) => {
        const value = normalizeTextAlign(align);
        const { from, to } = state.selection;
        let changed = false;

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (!this.options.types.includes(node.type.name)) return;
          if ((node.attrs.textAlign || null) === value) return;
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, textAlign: value });
          changed = true;
        });

        if (changed && dispatch) dispatch(tr);
        return changed;
      },
    };
  },

  addKeyboardShortcuts() {
    // 표 안에서 글자 정렬은 셀 정렬(extension-table의 align)이 맡는다. 문단 정렬이 셀 정렬을
    // 덮어써 셀 쪽이 먹지 않는 것처럼 보이기 때문이다. 그래서 같은 단축키를 표 안에서는
    // 표 자체의 좌우 위치에 쓴다 (툴바의 정렬 버튼도 같은 방식으로 동작한다).
    const align = (value) => () =>
      this.editor.isActive("table")
        ? value !== "justify" && this.editor.commands.setTableAlign(value === "left" ? null : value)
        : this.editor.commands.setTextAlign(value);

    return {
      "Mod-Shift-l": align("left"),
      "Mod-Shift-e": align("center"),
      "Mod-Shift-r": align("right"),
      "Mod-Shift-j": align("justify"),
    };
  },
});

// 브라우저 기본 기호(•, 1.)로는 낼 수 없는 목록 모양. 기호는 narrativeStyles의
// [data-list-style] 규칙이 그리므로, 저장 HTML에는 이 속성만 남으면 된다.
// (Word 임포트도 numbering.xml에서 읽은 모양을 같은 속성으로 넘겨준다)
export const LIST_STYLES = ["check", "paren"];

export const ListStyle = Extension.create({
  name: "listStyle",

  addOptions() {
    return { types: ["bulletList", "orderedList"] };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          listStyle: {
            default: null,
            parseHTML: (el) => {
              const value = el.getAttribute("data-list-style");
              return LIST_STYLES.includes(value) ? value : null;
            },
            // 번호를 이어받는 목록(<ol start="6">)은 "1)"을 세는 카운터의 시작점도 옮겨야
            // 한다. CSS가 start 속성을 읽지 못하므로 저장할 때 인라인으로 함께 적는다.
            renderHTML: (attrs) => {
              if (!attrs.listStyle) return {};

              const rendered = { "data-list-style": attrs.listStyle };
              if (attrs.listStyle === "paren" && attrs.start > 1) {
                rendered.style = `counter-reset: ${NARRATIVE_LIST_COUNTER} ${attrs.start - 1}`;
              }
              return rendered;
            },
          },
        },
      },
      {
        // 목록 중간에서 번호가 튀는 항목(Word에서 다른 목록이 이어 붙은 자리)은 그 항목이
        // 자기 번호를 들고 있어야 한다. 목록 모양은 항목에서 알 수 없으므로 카운터 시작점도
        // 늘 함께 적는다 — 카운터를 쓰지 않는 목록에서는 아무 일도 하지 않는다.
        types: ["listItem"],
        attributes: {
          value: {
            default: null,
            parseHTML: (el) => {
              const value = parseInt(el.getAttribute("value"), 10);
              return value > 0 ? value : null;
            },
            renderHTML: (attrs) =>
              attrs.value
                ? {
                    value: String(attrs.value),
                    style: `counter-reset: ${NARRATIVE_LIST_COUNTER} ${attrs.value - 1}`,
                  }
                : {},
          },
        },
      },
    ];
  },
});

const Superscript = Mark.create({
  name: "superscript",
  excludes: "subscript",

  parseHTML() {
    return [
      { tag: "sup" },
      { style: "vertical-align", getAttrs: (v) => (v === "super" ? null : false) },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["sup", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleSuperscript: () => ({ commands }) => commands.toggleMark(this.name),
    };
  },

  addKeyboardShortcuts() {
    return { "Mod-.": () => this.editor.commands.toggleSuperscript() };
  },
});

const Subscript = Mark.create({
  name: "subscript",
  excludes: "superscript",

  parseHTML() {
    return [
      { tag: "sub" },
      { style: "vertical-align", getAttrs: (v) => (v === "sub" ? null : false) },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["sub", HTMLAttributes, 0];
  },

  addCommands() {
    return {
      toggleSubscript: () => ({ commands }) => commands.toggleMark(this.name),
    };
  },

  addKeyboardShortcuts() {
    return { "Mod-,": () => this.editor.commands.toggleSubscript() };
  },
});

const MIN_COL_WIDTH = 40;

// 행 높이의 허용 범위. 실제 하한은 행마다 다르다(naturalRowHeight) — 이 값은 붙여 넣은
// HTML에 남은 터무니없이 작은 높이를 걸러내는 절대 하한이다.
const MIN_ROW_HEIGHT = 20;
const MAX_ROW_HEIGHT = 1000;

const clampRowHeight = (height) =>
  Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, Math.round(height)));

// px 또는 단위 없는 숫자만 받는다(뷰어와 계산 기준이 달라지는 pt/%/em은 버린다)
const normalizeRowHeight = (value) => {
  const match = /^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/i.exec(value || "");
  if (!match) return null;
  const height = Math.round(parseFloat(match[1]));
  return height >= MIN_ROW_HEIGHT ? Math.min(height, MAX_ROW_HEIGHT) : null;
};

// 정렬 아이콘. 유니코드에는 정렬용 글리프가 없어 직접 그린다.
// currentColor를 쓰므로 버튼이 활성화되면 색이 함께 바뀐다.
const AlignIcon = ({ anchor }) => (
  <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false">
    {[12, 7, 12, 7].map((short, i) => {
      // 양쪽 맞춤은 모든 줄이 같은 길이로 꽉 찬다
      const width = anchor === "justify" ? 12 : short;
      const x =
        anchor === "left" || anchor === "justify"
          ? 1
          : anchor === "right"
          ? 13 - width
          : (14 - width) / 2;
      return <rect key={i} x={x} y={2 + i * 3} width={width} height="1.6" rx="0.8" fill="currentColor" />;
    })}
  </svg>
);

// 셀 테두리 안에서 내용 덩어리가 어디에 붙는지 보여 준다
const VerticalAlignIcon = ({ anchor }) => (
  <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false">
    <rect
      x="1"
      y="1"
      width="12"
      height="12"
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      opacity="0.45"
    />
    <rect
      x="3.5"
      y={anchor === "top" ? 2.8 : anchor === "bottom" ? 8 : 5.4}
      width="7"
      height="3.2"
      rx="0.8"
      fill="currentColor"
    />
  </svg>
);

// 문단 정렬(표 밖·표 안 공통). 표 셀 정렬은 셀 전체에, 이쪽은 커서가 놓인 문단에 걸린다.
const TEXT_ALIGNS = [
  ["left", <AlignIcon anchor="left" />, "왼쪽 맞춤", "Ctrl+Shift+L"],
  ["center", <AlignIcon anchor="center" />, "가운데 맞춤", "Ctrl+Shift+E"],
  ["right", <AlignIcon anchor="right" />, "오른쪽 맞춤", "Ctrl+Shift+R"],
  ["justify", <AlignIcon anchor="justify" />, "양쪽 맞춤", "Ctrl+Shift+J"],
];

// 목록 [노드 이름, 기호 모양(기본 기호는 null), 버튼 글자, 설명]
const LIST_BUTTONS = [
  ["bulletList", null, "•", "글머리 기호 목록"],
  ["bulletList", "check", "✓", "체크 표시 목록"],
  ["orderedList", null, "1.", "번호 목록"],
  ["orderedList", "paren", "1)", "괄호 번호 목록"],
];

// 셀 정렬. 지정하지 않은 셀은 스타일시트 기본값(가로 왼쪽 / 세로 위)을 따른다.
const CELL_ALIGNS = [
  ["left", <AlignIcon anchor="left" />, "왼쪽 맞춤"],
  ["center", <AlignIcon anchor="center" />, "가운데 맞춤"],
  ["right", <AlignIcon anchor="right" />, "오른쪽 맞춤"],
];

// 테두리 자리 아이콘. 옅은 사각형 위에 지정될 변(과 안쪽 줄)을 진하게 덧그린다.
const BORDER_ICON_EDGES = {
  top: [2, 2, 12, 2],
  bottom: [2, 12, 12, 12],
  left: [2, 2, 2, 12],
  right: [12, 2, 12, 12],
};
const BORDER_ICON_INNER = [[7, 2, 7, 12], [2, 7, 12, 7]];

const BorderIcon = ({ anchor }) => {
  const edges = anchor === "all" || anchor === "outer" ? Object.keys(BORDER_ICON_EDGES) : [anchor];
  const inner = anchor === "all" || anchor === "inner" ? BORDER_ICON_INNER : [];

  return (
    <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true" focusable="false">
      <rect x="2" y="2" width="10" height="10" fill="none" stroke="currentColor" strokeOpacity="0.25" />
      {[...edges.map((edge) => BORDER_ICON_EDGES[edge]).filter(Boolean), ...inner].map((line, i) => (
        <line
          key={i}
          x1={line[0]}
          y1={line[1]}
          x2={line[2]}
          y2={line[3]}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="square"
        />
      ))}
    </svg>
  );
};

// 표 자체의 좌우 위치. 표 안에서는 문단 정렬 버튼이 이 역할을 맡는다(양쪽 맞춤은 없다).
// 열 너비를 지정해 표가 편집 영역보다 좁을 때만 눈에 보인다.
const TABLE_ALIGN_TITLES = {
  left: "표를 왼쪽에",
  center: "표를 가운데에",
  right: "표를 오른쪽에",
};

const CELL_VERTICAL_ALIGNS = [
  ["top", <VerticalAlignIcon anchor="top" />, "위쪽 맞춤"],
  ["middle", <VerticalAlignIcon anchor="middle" />, "세로 가운데 맞춤"],
  ["bottom", <VerticalAlignIcon anchor="bottom" />, "아래쪽 맞춤"],
];

const VERTICAL_ALIGN_VALUES = CELL_VERTICAL_ALIGNS.map(([value]) => value);

const normalizeVerticalAlign = (value) => {
  const normalized = (value || "").trim().toLowerCase();
  return VERTICAL_ALIGN_VALUES.includes(normalized) ? normalized : null;
};

const DEFAULT_TEXT_COLOR = "#000000";
const DEFAULT_TEXT_BG = "#ffff00";
const DEFAULT_CELL_BG = "#ffffff";

/**
 * 셀 테두리. 네 면을 각각 저장한다 — Word에서 흔한 "위아래만 굵은 선" 같은 표를
 * 그대로 옮겨야 하기 때문이다(툴바는 네 면을 한꺼번에 다룬다).
 *
 * 값은 인라인 style로 저장되므로 뷰어·사이트는 별도 규칙 없이 같은 모습을 낸다.
 * 지정하지 않은 면은 narrativeStyles의 기본 테두리를 그대로 쓰고, "none"은 그 기본을
 * 지운다(Word의 테두리 없는 표).
 */
const CELL_BORDER_SIDES = ["top", "right", "bottom", "left"];

// 테두리 굵기 선택 목록. "0"은 선을 그리지 않는다(= Word의 테두리 없음).
const CELL_BORDER_WIDTHS = [
  ["0", "없음"],
  ["1", "1px"],
  ["2", "2px"],
  ["3", "3px"],
  ["4", "4px"],
];

const BORDER_STYLES = new Set(["solid", "dashed", "dotted", "double"]);

const borderAttributeName = (side) => `border${side[0].toUpperCase()}${side.slice(1)}`;

// "2px solid #000000" 또는 "none". 인라인 style에서만 읽으므로 스타일시트 기본값은
// 여기에 잡히지 않는다(= 지정하지 않음).
const normalizeBorder = (style, width, color) => {
  const type = (style || "").trim().toLowerCase();
  if (!type) return null;
  if (type === "none" || type === "hidden") return "none";
  if (!BORDER_STYLES.has(type)) return null;

  const px = Math.round(parseFloat(width));
  if (!Number.isFinite(px) || px <= 0) return "none";

  const size = Math.min(px, MAX_CELL_BORDER_WIDTH);
  // 브라우저는 인라인 색을 rgb()로 정규화해 돌려준다. hex로 되돌려 두지 않으면 저장된
  // HTML을 다시 열 때마다 값이 달라져 변경 이력에 서식이 바뀐 것처럼 잡힌다.
  const raw = (color || "").trim() || CELL_BORDER_COLOR;
  return `${size}px ${type} ${toInputColor(raw, raw)}`;
};

// 저장된 값을 툴바가 다룰 수 있는 조각으로 되돌린다. 지정하지 않았으면 null.
const parseBorder = (value) => {
  if (!value) return null;
  if (value === "none") return { width: 0, style: "solid", color: CELL_BORDER_COLOR };

  const match = /^(\d+)px\s+(\w+)\s+(.+)$/.exec(value);
  if (!match) return null;
  return { width: parseInt(match[1], 10), style: match[2], color: match[3] };
};

// 네 면이 모두 같은 값일 때만 그 값. 섞여 있거나 없으면 null.
const uniformBorder = (attrs) => {
  const values = CELL_BORDER_SIDES.map((side) => attrs[borderAttributeName(side)] || null);
  return values.every((value) => value && value === values[0]) ? values[0] : null;
};

const borderAttribute = (side) => {
  const name = borderAttributeName(side); // borderTop
  const css = name.slice("border".length); // Top

  return {
    default: null,
    parseHTML: (el) =>
      normalizeBorder(el.style[`border${css}Style`], el.style[`border${css}Width`], el.style[`border${css}Color`]),
    renderHTML: (attrs) => (attrs[name] ? { style: `border-${side}: ${attrs[name]}` } : {}),
  };
};

const toHex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

// <input type="color">는 6자리 hex만 받는다. 저장된 HTML을 다시 읽으면 브라우저가
// 배경색을 rgb() 형태로 정규화해 두므로 그것도 변환해 준다. 변환할 수 없는 값
// (색상명 등)은 기본값으로 대체하고, 스와치에는 원래 색을 그대로 보여 준다.
const toInputColor = (color, fallback) => {
  if (!color) return fallback;
  const value = color.trim();

  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }

  const rgb = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (rgb) return `#${toHex2(+rgb[1])}${toHex2(+rgb[2])}${toHex2(+rgb[3])}`;

  return fallback;
};

// 커서가 놓인 표 노드와 문서상 위치
const findTableAt = (state) => {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "table") return { node, pos: $from.before(depth) };
  }
  return null;
};

// 셀 선택(CellSelection) 중이면 선택한 셀 전부, 아니면 커서가 놓인 셀 하나
const selectedCells = (state) => {
  const { selection } = state;

  // CellSelection만 갖는 메서드. 클래스를 import하지 않고 이것으로 구분한다.
  if (selection.forEachCell) {
    const cells = [];
    selection.forEachCell((node, pos) => cells.push({ node, pos }));
    return cells;
  }

  const { $from } = selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      return [{ node, pos: $from.before(depth) }];
    }
  }
  return [];
};

/**
 * 테두리를 붙일 자리. 셀 하나가 아니라 "선택 영역"을 기준으로 삼는다.
 * 여러 셀을 골랐을 때 위쪽이라 하면 선택한 덩어리의 윗변이지 셀마다의 윗변이 아니다.
 */
const BORDER_TARGETS = [
  ["all", "모두"],
  ["outer", "바깥쪽"],
  ["inner", "안쪽"],
  ["top", "위쪽"],
  ["bottom", "아래쪽"],
  ["left", "왼쪽"],
  ["right", "오른쪽"],
];

// 선택 영역 안에서 이 셀이 맞닿은 변. TableMap의 right/bottom은 열린 구간이라
// 선택 영역의 값과 그대로 비교된다.
const cellEdges = (rect, bounds) => ({
  top: rect.top === bounds.top,
  bottom: rect.bottom === bounds.bottom,
  left: rect.left === bounds.left,
  right: rect.right === bounds.right,
});

const borderSidesFor = (target, rect, bounds) => {
  if (target === "all") return CELL_BORDER_SIDES;

  const edges = cellEdges(rect, bounds);
  if (target === "outer") return CELL_BORDER_SIDES.filter((side) => edges[side]);
  // 안쪽 줄은 맞닿은 두 셀이 같은 값을 갖는다(테두리가 겹쳐 그려지는 collapse 방식)
  if (target === "inner") return CELL_BORDER_SIDES.filter((side) => !edges[side]);

  return edges[target] ? [target] : [];
};

// 표의 index번째 행 위치. 행마다 크기가 다르므로 앞에서부터 더해 간다.
const rowPosAt = (table, tableStart, index) => {
  let pos = tableStart;
  for (let i = 0; i < index; i += 1) pos += table.child(i).nodeSize;
  return pos;
};

// 선택 영역이 걸쳐 있는 표 행들.
//
// 세로 병합(rowspan)된 셀은 시작 행에만 들어 있지만 화면에서는 여러 행에 걸쳐 그려지고,
// 그 높이는 걸쳐 있는 행 높이의 합이다. 그래서 시작 행에만 높이를 걸면 지정한 값과 보이는
// 높이가 어긋나고, 나머지 행에는 예전 높이가 그대로 남는다. 걸쳐 있는 행을 모두 돌려주어
// 표 전체가 고르게 지정되도록 한다(Word의 행 높이도 같은 방식이다).
const selectedRows = (state) => {
  const rows = new Map();

  selectedCells(state).forEach(({ node, pos }) => {
    // 셀 바로 앞 위치를 resolve하면 부모가 행이다
    const $cell = state.doc.resolve(pos);
    const $row = state.doc.resolve($cell.before());
    const table = $row.parent;
    const tableStart = $row.start();

    const first = $row.index();
    const last = Math.min(first + (node.attrs.rowspan || 1) - 1, table.childCount - 1);

    for (let index = first; index <= last; index += 1) {
      const row = table.child(index);
      // 셀이 모두 위로 병합돼 빈 tr만 남은 행은 높이를 가질 수 없다
      if (!row.childCount) continue;

      const rowPos = rowPosAt(table, tableStart, index);
      if (!rows.has(rowPos)) rows.set(rowPos, { pos: rowPos, node: row });
    }
  });

  return [...rows.values()];
};

// 행이 내용만으로 갖는 높이.
//
// HTML에서 tr의 height는 최소 높이라, 내용이 차지하는 높이보다 작은 값을 지정하면 화면에
// 아무 변화가 없다(한 줄짜리 셀도 글자 22.4 + 패딩 12 + 테두리로 36px 남짓 된다).
// 그래서 입력칸과 드래그 모두 이 값을 하한으로 쓴다. 지정된 높이를 잠깐 지우고 재지만
// 그 사이에 화면이 다시 그려지지는 않는다.
const naturalRowHeight = (view, pos) => {
  const dom = view.nodeDOM(pos);
  if (!dom || !dom.style || !dom.getBoundingClientRect) return MIN_ROW_HEIGHT;

  const specified = dom.style.height;
  dom.style.height = "";
  const height = Math.ceil(dom.getBoundingClientRect().height);
  dom.style.height = specified;

  return Math.max(MIN_ROW_HEIGHT, height);
};

// 패딩을 제외한 편집 영역의 실제 가용 너비
const contentWidth = (editor) => {
  const dom = editor.view.dom;
  const style = window.getComputedStyle(dom);
  return dom.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
};

// border-collapse 표는 바깥 테두리의 절반이 열 너비 밖에 붙어, 실제 폭이 열 너비 합보다
// 1px 남짓 넓게 그려진다. 열 너비 합을 편집 영역 폭에 딱 맞추면 그 1px 때문에
// .tableWrapper에 가로 스크롤이 생기므로, 상한에서 미리 테두리 몫을 빼 둔다.
const TABLE_BORDER_ALLOWANCE = 2;

// 열 너비의 합이 넘어서는 안 되는 상한
const maxTableWidth = (editor) => contentWidth(editor) - TABLE_BORDER_ALLOWANCE;

// 표의 열 인덱스별 너비. 지정되지 않은 열은 0으로 남는다(colspan은 펼쳐서 센다).
const collectColumnWidths = (table) => {
  const widths = [];
  let totalCols = 0;
  table.forEach((row) => {
    let col = 0;
    row.forEach((cell) => {
      const span = cell.attrs.colspan || 1;
      const colwidth = cell.attrs.colwidth;
      for (let i = 0; i < span; i += 1, col += 1) {
        if (!widths[col] && colwidth && colwidth[i]) widths[col] = colwidth[i];
      }
    });
    totalCols = Math.max(totalCols, col);
  });
  // 성긴 배열은 filter/some/map이 빈 칸을 건너뛰므로 0으로 메워 돌려준다
  return Array.from({ length: totalCols }, (_, i) => widths[i] || 0);
};

// 모든 열의 너비가 지정된 표만 배열을 돌려준다. 한 열이라도 비면 tiptap이 "가변 폭"으로
// 그리므로 폭 계산을 할 수 없다.
const fixedColumnWidths = (table) => {
  const widths = collectColumnWidths(table);
  if (!widths.length || widths.some((width) => !width)) return null;
  return widths;
};

// 열 너비를 셀 attrs에 되돌려 쓴다(colspan 셀은 자기 몫만큼 잘라서 받는다)
const applyColumnWidths = (tr, table, pos, widths) => {
  table.forEach((row, rowOffset) => {
    const rowPos = pos + 1 + rowOffset;
    let col = 0;
    row.forEach((cell, cellOffset) => {
      const span = cell.attrs.colspan || 1;
      const next = [];
      for (let i = 0; i < span; i += 1, col += 1) next.push(widths[col]);
      const current = cell.attrs.colwidth;
      const same = current && current.length === span && next.every((w, i) => w === current[i]);
      if (same) return;
      tr.setNodeMarkup(rowPos + 1 + cellOffset, undefined, { ...cell.attrs, colwidth: next });
    });
  });
};

// 열 너비의 합이 편집 영역을 넘으면 폭 안에 맞춘다.
// 최소 너비에 걸린 열은 건드리지 않고, 그 위로 남은 여유에 비례해서 줄인다.
const shrinkToFit = (widths, maxTotal) => {
  const excess = widths.reduce((sum, width) => sum + width, 0) - maxTotal;
  if (!maxTotal || excess <= 0) return widths;

  const slack = widths.map((width) => Math.max(0, width - MIN_COL_WIDTH));
  const totalSlack = slack.reduce((sum, room) => sum + room, 0);
  if (totalSlack <= 0) return widths; // 이미 다 최소 너비라 더 줄일 수 없다
  if (excess >= totalSlack) return widths.map((width, i) => width - slack[i]);

  const next = widths.map((width, i) => width - Math.floor((slack[i] * excess) / totalSlack));

  // 내림 때문에 남은 픽셀은 아직 여유가 있는 열에서 1px씩 회수한다
  let rest = next.reduce((sum, width) => sum + width, 0) - maxTotal;
  while (rest > 0) {
    const before = rest;
    for (let i = 0; i < next.length && rest > 0; i += 1) {
      if (next[i] <= MIN_COL_WIDTH) continue;
      next[i] -= 1;
      rest -= 1;
    }
    if (rest === before) break; // 더 줄일 곳이 없다
  }
  return next;
};

// col 열을 width로 만들되 표 전체 폭이 편집 영역을 넘지 않게 한다.
// 남은 자리를 먼저 쓰고, 모자라면 오른쪽 열에서 가까운 순으로 가져온다(Word와 같은 동작).
// 마지막 열은 가져올 오른쪽 열이 없으므로 남은 자리만큼만 늘어난다.
const resizeColumnTo = (base, col, width, maxTotal) => {
  const next = [...base];
  const last = base.length - 1;
  const free = Math.max(0, maxTotal - base.reduce((sum, w) => sum + w, 0));

  if (col === last) {
    next[last] = Math.max(MIN_COL_WIDTH, Math.min(width, base[last] + free));
    return next;
  }

  const room = free + base.slice(col + 1).reduce((sum, w) => sum + w - MIN_COL_WIDTH, 0);
  next[col] = Math.max(MIN_COL_WIDTH, Math.min(width, base[col] + room));

  const delta = next[col] - base[col];
  if (delta < 0) {
    // 줄인 폭은 바로 오른쪽 열이 가져가 표 전체 폭이 유지된다
    next[col + 1] = base[col + 1] - delta;
    return next;
  }

  let need = delta - free; // 남은 자리로 못 채운 만큼만 오른쪽에서 회수한다
  for (let i = col + 1; i <= last && need > 0; i += 1) {
    const take = Math.min(base[i] - MIN_COL_WIDTH, need);
    next[i] = base[i] - take;
    need -= take;
  }
  return next;
};

// 리사이즈 결과가 편집 영역을 넘으면 넘긴 만큼을 오른쪽 열에서 회수한다.
// 어느 열을 끌었는지 알 수 없으면(임포트 등) 전체를 비율대로 줄인다.
const fitResizedWidths = (widths, previous, maxTotal) => {
  if (widths.reduce((sum, w) => sum + w, 0) <= maxTotal) return null;

  const grown =
    previous && previous.length === widths.length
      ? widths.findIndex((width, i) => width > previous[i])
      : -1;
  if (grown === -1) return shrinkToFit(widths, maxTotal);

  return resizeColumnTo(shrinkToFit(previous, maxTotal), grown, widths[grown], maxTotal);
};

// prosemirror-tables의 열 리사이즈는 끄는 열만 넓히고 표를 그만큼 옆으로 늘린다.
// table-layout: fixed에서는 열 너비의 합이 max-width보다 우선하므로 표가 편집 영역을
// 넘어가 버린다. 그래서 마우스를 놓는 순간(= 너비가 attrs에 반영되는 트랜잭션) 폭을 맞춘다.
const fitResizedTables = (editor, oldState, newState) => {
  // 리사이즈는 셀 attrs만 바꾸므로 문서 길이가 그대로다. 그 밖의 편집은 건드리지 않는다.
  if (!editor.view || oldState.doc.nodeSize !== newState.doc.nodeSize) return null;

  const maxTotal = maxTableWidth(editor);
  if (!maxTotal || maxTotal <= 0) return null;

  let tr = null;
  newState.doc.descendants((node, pos) => {
    if (node.type.name !== "table") return true;

    const widths = fixedColumnWidths(node);
    if (!widths) return false;

    const before = oldState.doc.nodeAt(pos);
    const previous = before && before.type === node.type ? fixedColumnWidths(before) : null;
    const fitted = fitResizedWidths(widths, previous, maxTotal);
    if (!fitted) return false;

    tr = tr || newState.tr;
    applyColumnWidths(tr, node, pos, fitted);
    return false;
  });
  return tr;
};

// 열/행 추가·분할로 생긴 셀은 colwidth가 비어 있다. 열이 하나라도 비면 tiptap이 표를
// "가변 폭"으로 판정해 인라인 width를 지우고, 그러면 지정해 둔 열 너비가 무시된 채
// 표가 컨테이너 전체로 늘어난다. 그래서 새로 생긴 빈 셀만 골라 너비를 채워준다.
const fillMissingColumnWidths = (editor) => {
  const found = findTableAt(editor.state);
  if (!found) return;
  const { node, pos } = found;

  const widths = collectColumnWidths(node);
  const known = widths.filter(Boolean);
  if (!known.length || known.length === widths.length) return; // 채울 것이 없다

  // 기존 열은 지정된 너비를 그대로 두고, 새 열에는 남은 공간을 나눠 준다
  const maxTotal = maxTableWidth(editor);
  const missingCols = widths.length - known.length;
  const remaining = maxTotal - known.reduce((sum, w) => sum + w, 0);
  const fallback = Math.max(MIN_COL_WIDTH, Math.floor(remaining / missingCols));

  // 남은 공간이 모자라면(꽉 찬 표에 열을 더한 경우) 전체를 비율대로 줄여 폭 안에 맞춘다
  const filled = shrinkToFit(
    widths.map((width) => width || fallback),
    maxTotal
  );

  const { tr } = editor.state;
  applyColumnWidths(tr, node, pos, filled);
  if (tr.docChanged) editor.view.dispatch(tr);
};

// 화면에 그려진 열 너비를 읽는다. colspan 셀은 어느 열이 몇 px인지 알 수 없으므로
// colspan 1인 셀만 재고, 그런 셀이 하나도 없는 열은 0으로 남는다.
const measuredColumnWidths = (view, table, start) => {
  const map = TableMap.get(table);
  const widths = new Array(map.width).fill(0);

  for (let col = 0; col < map.width; col += 1) {
    for (let row = 0; row < map.height && !widths[col]; row += 1) {
      const cellPos = map.map[row * map.width + col];
      if ((table.nodeAt(cellPos).attrs.colspan || 1) !== 1) continue;
      const dom = view.nodeDOM(start + cellPos);
      if (dom && dom.getBoundingClientRect) {
        widths[col] = Math.round(dom.getBoundingClientRect().width);
      }
    }
  }
  return widths;
};

// 너비가 비어 있는 열이 하나라도 있으면 tiptap이 표에 인라인 width를 붙이지 않아, 표는
// 스타일시트의 width: 100%로 그려진다. 그 상태에서 열 경계를 끌면 prosemirror-tables가
// 끈 열에만 colwidth를 넣는데 표 폭은 여전히 100%라, 표가 좁아지는 대신 나머지 열이
// 남은 폭을 나눠 가지며 안쪽 경계만 움직인다.
// 그래서 드래그가 시작되기 전에 지금 그려져 있는 너비를 그대로 attrs에 굳혀 고정 폭 표로
// 만들어 둔다. 보이는 모습은 그대로면서 이후 동작은 다른 표와 같아진다.
const freezeFluidColumnWidths = (editor) => {
  const { view } = editor;
  const resize = columnResizingPluginKey.getState(view.state);
  if (!resize || resize.activeHandle < 0 || resize.dragging) return; // 열 리사이즈가 아니다

  const $cell = view.state.doc.resolve(resize.activeHandle);
  const table = $cell.node(-1);
  const widths = collectColumnWidths(table);
  if (!widths.some((width) => !width)) return; // 이미 모든 열이 고정 폭이다

  const measured = measuredColumnWidths(view, table, $cell.start(-1));
  const frozen = widths.map((width, i) => width || measured[i]);
  if (frozen.some((width) => !width)) return; // 너비를 못 읽은 열이 있으면 손대지 않는다

  const tr = view.state.tr;
  applyColumnWidths(tr, table, $cell.before(-1), shrinkToFit(frozen, maxTableWidth(editor)));
  if (tr.docChanged) view.dispatch(tr);
};

// 표 자체의 좌우 위치. Word에서 가운데·오른쪽에 놓인 표를 가져올 때 쓰며(wordImport),
// 열 너비가 지정돼 본문보다 좁은 표에서만 눈에 보인다.
const tableAlignStyle = (align) => {
  const margins = TABLE_ALIGN_MARGINS[align];
  return margins ? `margin-left: ${margins.left}; margin-right: ${margins.right}` : null;
};

const parseTableAlign = (el) => {
  if ((el.style.marginLeft || "").trim().toLowerCase() !== "auto") return null;
  return (el.style.marginRight || "").trim().toLowerCase() === "auto" ? "center" : "right";
};

// 편집 화면에서 표를 그리는 것은 렌더된 HTML이 아니라 이 뷰다(리사이즈 핸들 때문).
// 여기서도 같은 여백을 붙여야 편집 화면과 조회 화면의 표 위치가 같아진다.
class AlignedTableView extends TableView {
  constructor(node, cellMinWidth, view) {
    super(node, cellMinWidth, view);
    this.applyAlign(node);
  }

  applyAlign(node) {
    const margins = TABLE_ALIGN_MARGINS[node.attrs.align] || { left: "", right: "" };
    this.table.style.marginLeft = margins.left;
    this.table.style.marginRight = margins.right;
  }

  update(node) {
    const updated = super.update(node);
    if (updated) this.applyAlign(node);
    return updated;
  }
}

// Tab으로 마지막 셀에서 행이 추가될 때도 열 너비가 유지되도록 후처리를 건다
const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: parseTableAlign,
        renderHTML: (attrs) => {
          const style = tableAlignStyle(attrs.align);
          return style ? { style } : {};
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),

      /**
       * 선택한 셀들의 테두리를 자리별로 지정한다.
       *
       * @param target "all" | "outer" | "inner" | "top" | "bottom" | "left" | "right"
       * @param value  "2px solid #000000" 같은 값, 선 없음은 "none", 지정 지움은 null
       */
      setCellBorders: (target, value) => ({ state, tr, dispatch }) => {
        const table = findTableAt(state);
        const cells = selectedCells(state);
        if (!table || !cells.length) return false;

        const map = TableMap.get(table.node);
        const start = table.pos + 1;
        const rects = cells.map((cell) => ({ ...cell, rect: map.findCell(cell.pos - start) }));

        const bounds = rects.reduce(
          (acc, { rect }) => ({
            top: Math.min(acc.top, rect.top),
            left: Math.min(acc.left, rect.left),
            bottom: Math.max(acc.bottom, rect.bottom),
            right: Math.max(acc.right, rect.right),
          }),
          { top: Infinity, left: Infinity, bottom: -Infinity, right: -Infinity }
        );

        let changed = false;
        rects.forEach(({ node, pos, rect }) => {
          const sides = borderSidesFor(target, rect, bounds);
          if (!sides.length) return;

          const attrs = { ...node.attrs };
          sides.forEach((side) => {
            attrs[borderAttributeName(side)] = value;
          });
          if (sides.some((side) => node.attrs[borderAttributeName(side)] !== value)) {
            tr.setNodeMarkup(pos, undefined, attrs);
            changed = true;
          }
        });

        if (changed && dispatch) dispatch(tr);
        return changed;
      },

      // 표 자체의 좌우 위치. 셀이 아니라 표 노드의 속성이라 표 노드를 직접 고친다.
      setTableAlign: (align) => ({ state, tr, dispatch }) => {
        const found = findTableAt(state);
        if (!found) return false;

        const { node, pos } = found;
        const next = align || null;
        if ((node.attrs.align || null) === next) return false;

        if (dispatch) dispatch(tr.setNodeMarkup(pos, undefined, { ...node.attrs, align: next }));
        return true;
      },
    };
  },

  // Table의 renderHTML은 style 속성이 오면 표 너비(width) 대신 그것만 내보낸다.
  // 정렬 때문에 열 너비가 사라지지 않도록, 부모에게는 style을 빼고 넘긴 뒤 뒤에 덧붙인다.
  renderHTML({ node, HTMLAttributes }) {
    const { style, ...rest } = HTMLAttributes;
    const rendered = this.parent?.({ node, HTMLAttributes: rest });
    if (style && rendered) rendered[1].style = [rendered[1].style, style].filter(Boolean).join("; ");
    return rendered;
  },

  addKeyboardShortcuts() {
    const parent = this.parent?.() || {};
    return {
      ...parent,
      Tab: () => {
        const handled = parent.Tab ? parent.Tab() : false;
        if (handled) fillMissingColumnWidths(this.editor);
        return handled;
      },
    };
  },

  addProseMirrorPlugins() {
    const { editor } = this;
    // 가변 폭 표를 끌기 시작하면 먼저 고정 폭으로 굳힌다. prosemirror-tables의 mousedown보다
    // 앞서야 드래그로 바뀌기 전의 너비를 읽을 수 있으므로 플러그인 목록 맨 앞에 둔다.
    // (prosemirror-tables의 핸들러는 값을 돌려주지 않으므로 이 핸들러도 함께 불린다)
    const freezeColumnWidths = new Plugin({
      key: new PluginKey("freezeColumnWidths"),
      props: {
        handleDOMEvents: {
          mousedown: () => {
            freezeFluidColumnWidths(editor);
            return false;
          },
        },
      },
    });
    // 리사이즈 자체는 prosemirror-tables가 그대로 처리하고(핸들 표시·드래그),
    // 결과 너비만 여기서 편집 영역 폭에 맞춘다.
    const fitTableWidth = new Plugin({
      key: new PluginKey("fitTableWidth"),
      appendTransaction: (transactions, oldState, newState) => {
        if (!transactions.some((tr) => tr.docChanged)) return null;
        return fitResizedTables(editor, oldState, newState);
      },
    });
    return [freezeColumnWidths, ...(this.parent?.() || []), fitTableWidth];
  },
});

// 셀 배경색·세로 정렬·테두리를 인라인 스타일로 저장한다. 가로 정렬(text-align)은
// extension-table의 align 속성이 이미 처리하므로 따로 만들지 않는다.
// (여러 attribute가 style을 내보내도 mergeAttributes가 선언 단위로 합쳐 준다)
const cellStyleAttributes = (parentAttributes) => ({
  ...parentAttributes,
  ...Object.fromEntries(CELL_BORDER_SIDES.map((side) => [borderAttributeName(side), borderAttribute(side)])),
  backgroundColor: {
    default: null,
    // 테두리 색과 같은 이유로 hex로 되돌려 둔다. 브라우저가 rgb()로 정규화한 값을 그대로
    // 저장하면 열었다 저장할 때마다 값이 달라져 변경 이력에 서식 변경이 쌓인다.
    parseHTML: (el) => {
      const color = el.style.backgroundColor;
      return color ? toInputColor(color, color) : null;
    },
    renderHTML: (attrs) =>
      attrs.backgroundColor ? { style: `background-color: ${attrs.backgroundColor}` } : {},
  },
  verticalAlign: {
    default: null,
    // 인라인 스타일만 읽는다. 스타일시트의 기본 top은 여기에 잡히지 않는다.
    parseHTML: (el) => normalizeVerticalAlign(el.style.verticalAlign),
    renderHTML: (attrs) =>
      attrs.verticalAlign ? { style: `vertical-align: ${attrs.verticalAlign}` } : {},
  },
});

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return cellStyleAttributes(this.parent?.());
  },
});

const CustomTableHeader = TableHeader.extend({
  addAttributes() {
    return cellStyleAttributes(this.parent?.());
  },
});

// 행 경계에서 리사이즈 핸들이 잡히는 범위(경계 위아래 px)
const ROW_RESIZE_ZONE = 5;

const rowResizeKey = new PluginKey("rowResize");

const setRowResizeState = (view, value) => {
  view.dispatch(view.state.tr.setMeta(rowResizeKey, value));
};

// rowPos 행의 아래 경계에서 실제로 끝나는 셀들.
//
// 그 행에 들어 있는 셀이 곧 경계에 닿는 셀은 아니다. 아래로 뻗는 병합 셀은 바닥이 경계보다
// 아래에 있고(그 셀까지 칠하면 강조선이 두 높이로 갈라진다), 반대로 위 행에서 내려와 이
// 행에서 끝나는 병합 셀은 다른 tr에 들어 있다. 그래서 행이 아니라 "이 경계에서 끝나는가"로
// 셀을 고른다. 병합 셀 한가운데를 지나는 열은 경계선이 없으므로 자연히 빠진다.
const cellsEndingAtRow = (doc, rowPos) => {
  const $row = doc.resolve(rowPos);
  const table = $row.parent;
  const index = $row.index();

  const cells = [];
  let pos = $row.start();

  for (let i = 0; i <= index; i += 1) {
    const row = table.child(i);
    const rowStart = pos;
    row.forEach((cell, offset) => {
      if (i + (cell.attrs.rowspan || 1) - 1 === index) {
        cells.push({ pos: rowStart + 1 + offset, node: cell });
      }
    });
    pos += row.nodeSize;
  }

  return cells;
};

// 마우스가 놓인 셀의 위/아래 경계를 보고 그 경계가 어느 행의 아래 경계인지 찾는다.
//
// 기준을 행(tr)이 아니라 셀로 잡는 이유는 세로 병합(rowspan) 때문이다. 병합된 셀은
// 시작 행의 tr에 들어 있지만 화면에서는 여러 행에 걸쳐 그려진다. tr 상자를 기준으로 하면
// 눈에 보이는 선(= 병합 셀의 아래 테두리)에서는 핸들이 잡히지 않고, 병합 셀 한가운데의
// 아무것도 없는 자리에서 엉뚱한 행이 잡힌다.
//
// 셀의 아래 테두리는 그 셀이 걸쳐 있는 마지막 행의 경계이고, 위 테두리는 바로 위 행의
// 경계다. 병합 셀 한가운데에는 그 열에 경계선이 없으므로 핸들도 두지 않는다(같은 경계를
// 병합되지 않은 옆 열에서 잡을 수 있다).
const rowResizeTarget = (view, event) => {
  const cellDom = event.target instanceof Element ? event.target.closest("td, th") : null;
  if (!cellDom || !view.dom.contains(cellDom)) return null;

  // 셀 DOM 안쪽 0번 위치 = 셀 내용의 시작. 그 앞이 셀 노드 자체의 위치다.
  const cellPos = view.posAtDOM(cellDom, 0) - 1;
  const cell = view.state.doc.nodeAt(cellPos);
  if (!cell || !["tableCell", "tableHeader"].includes(cell.type.name)) return null;

  const rect = cellDom.getBoundingClientRect();
  const atBottom = Math.abs(event.clientY - rect.bottom) <= ROW_RESIZE_ZONE;
  const atTop = Math.abs(event.clientY - rect.top) <= ROW_RESIZE_ZONE;
  if (!atBottom && !atTop) return null;

  const $cell = view.state.doc.resolve(cellPos);
  const $row = view.state.doc.resolve($cell.before());
  const table = $row.parent;
  // 아래 테두리면 셀이 끝나는 행, 위 테두리면 바로 위 행이 조절 대상이다
  const covered = $row.index() + (cell.attrs.rowspan || 1) - 1;
  let index = atBottom ? Math.min(covered, table.childCount - 1) : $row.index() - 1;
  if (index < 0) return null; // 표의 첫 행 위쪽에는 조절할 경계가 없다

  // 셀이 모두 위로 병합돼 빈 tr만 남은 행은 높이를 가질 수 없다. 셀이 남아 있는
  // 위쪽 행까지 거슬러 올라가야 끄는 대로 병합 셀의 높이가 늘어난다.
  while (index >= 0 && !table.child(index).childCount) index -= 1;
  if (index < 0) return null;

  const pos = rowPosAt(table, $row.start(), index);
  const dom = view.nodeDOM(pos);
  if (!dom) return null;
  return { pos, node: table.child(index), dom };
};

// 행 경계를 끌어 높이를 조절한다. prosemirror-tables의 열 리사이즈에는 대응하는 기능이
// 없어 직접 만든다.
//
// 끄는 동안에는 문서를 건드리지 않고 데코레이션의 인라인 style로만 높이를 미리 보여 준다.
// (매 mousemove마다 setNodeMarkup을 하면 실행 취소 기록이 드래그 한 번에 수십 개 쌓인다)
// 실제 rowHeight 속성은 마우스를 놓을 때 한 번만 쓴다.
const rowResizingPlugin = () =>
  new Plugin({
    key: rowResizeKey,
    state: {
      init: () => ({ hover: null, drag: null }),
      apply: (tr, value) => {
        const meta = tr.getMeta(rowResizeKey);
        const next = meta === undefined ? value : { ...value, ...meta };
        // 문서가 바뀌면 잡아 둔 위치가 어긋나므로 접는다(리사이즈 중에는 편집이 없다)
        if (tr.docChanged && (next.hover || next.drag)) return { hover: null, drag: null };
        return next;
      },
    },
    props: {
      attributes: (state) => {
        const { hover, drag } = rowResizeKey.getState(state);
        return hover || drag ? { class: "row-resize-cursor" } : null;
      },

      decorations: (state) => {
        const { hover, drag } = rowResizeKey.getState(state);
        const active = drag || hover;
        if (!active) return null;

        const row = state.doc.nodeAt(active.pos);
        if (!row || row.type.name !== "tableRow") return null;

        // 강조선은 그 경계에서 실제로 끝나는 셀에만 그린다
        const decorations = cellsEndingAtRow(state.doc, active.pos).map(({ pos, node }) =>
          Decoration.node(pos, pos + node.nodeSize, { class: "row-resize-edge" })
        );

        // 끄는 동안의 미리보기 높이. 데코레이션 style은 행이 이미 가진 style 뒤에 붙어
        // 높이를 덮어쓴다.
        if (drag) {
          decorations.push(
            Decoration.node(active.pos, active.pos + row.nodeSize, {
              style: `height: ${drag.height}px`,
            })
          );
        }

        return DecorationSet.create(state.doc, decorations);
      },

      handleDOMEvents: {
        mousemove: (view, event) => {
          if (!view.editable || rowResizeKey.getState(view.state).drag) return false;

          const target = rowResizeTarget(view, event);
          const current = rowResizeKey.getState(view.state).hover;
          const pos = target ? target.pos : null;
          if ((current ? current.pos : null) !== pos) {
            setRowResizeState(view, { hover: target ? { pos } : null });
          }
          return false;
        },

        mouseleave: (view) => {
          const { hover, drag } = rowResizeKey.getState(view.state);
          if (hover && !drag) setRowResizeState(view, { hover: null });
          return false;
        },

        mousedown: (view, event) => {
          if (!view.editable || event.button !== 0) return false;
          if (rowResizeKey.getState(view.state).drag) return false;

          const target = rowResizeTarget(view, event);
          if (!target) return false;

          // 끄는 동안 글자가 선택되지 않도록 기본 동작을 막는다
          event.preventDefault();

          const { pos } = target;
          const startY = event.clientY;
          const startHeight = Math.round(target.dom.getBoundingClientRect().height);
          // 내용보다 작게는 그려지지 않으므로 거기서 멈춘다. 하한을 더 낮게 잡으면
          // 경계선 미리보기만 커서를 따라가고 행은 그대로라 "안 먹는" 것처럼 보인다.
          const minHeight = naturalRowHeight(view, pos);
          const heightAt = (e) =>
            Math.min(MAX_ROW_HEIGHT, Math.max(minHeight, startHeight + e.clientY - startY));

          setRowResizeState(view, { hover: null, drag: { pos, height: startHeight } });

          const stop = () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
          };

          // 에디터가 사라졌거나(다른 화면으로 이동) 상태가 접혔으면 더 진행하지 않는다
          const dropped = () => !view.docView || !rowResizeKey.getState(view.state).drag;

          const move = (e) => {
            if (dropped()) {
              stop();
              return;
            }
            setRowResizeState(view, { drag: { pos, height: heightAt(e) } });
          };

          const up = (e) => {
            stop();
            if (dropped()) return;

            const tr = view.state.tr.setMeta(rowResizeKey, { hover: null, drag: null });
            const node = tr.doc.nodeAt(pos);
            // 끌지 않고 경계를 그냥 클릭했다면 지금 높이를 굳이 고정하지 않는다
            if (e.clientY !== startY && node && node.type.name === "tableRow") {
              // 내용 높이까지 끌어올리면 지정을 지워 내용에 맞는 높이로 되돌린다
              const height = heightAt(e);
              const next = height <= minHeight ? null : height;
              if ((node.attrs.rowHeight || null) !== next) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, rowHeight: next });
              }
            }
            view.dispatch(tr);
          };

          window.addEventListener("mousemove", move);
          window.addEventListener("mouseup", up);
          return true;
        },
      },
    },
  });

// 행 높이는 tr의 인라인 style(height)로 저장한다. HTML에서 tr의 height는 최소 높이라
// 내용이 더 길면 행이 늘어난다(Word의 "최소값" 행 높이와 같은 동작). 인라인 스타일이라
// 뷰어·사이트는 별도 규칙 없이 저장된 HTML 그대로 같은 높이를 낸다.
const CustomTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      rowHeight: {
        default: null,
        // 외부에서 붙여 넣은 HTML은 style 대신 height 속성을 쓰기도 한다
        parseHTML: (el) => normalizeRowHeight(el.style.height || el.getAttribute("height")),
        renderHTML: (attrs) => (attrs.rowHeight ? { style: `height: ${attrs.rowHeight}px` } : {}),
      },
    };
  },

  addProseMirrorPlugins() {
    return [...(this.parent?.() || []), rowResizingPlugin()];
  },
});

// 표 확장 묶음. 저장된 HTML을 어떻게 읽고 쓰는지가 여기서 정해지므로 테스트도 이것을 쓴다.
export const tableExtensions = [
  CustomTable.configure({
    resizable: true,
    handleWidth: 8,
    cellMinWidth: MIN_COL_WIDTH,
    View: AlignedTableView,
  }),
  CustomTableRow,
  CustomTableHeader,
  CustomTableCell,
];

// 툴바까지 포함한 상자 전체를 사이트의 내러티브 상자와 같은 폭으로 맞춘다
const Wrapper = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 6px;
  ${narrativeBoxStyles}
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  border-radius: 6px 6px 0 0;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
`;

const Btn = styled.button`
  padding: 3px 8px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: ${(p) => (p.$active ? "#0d6efd" : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : "#495057")};
  font-size: 13px;
  cursor: pointer;
  min-width: 28px;
  &:hover {
    background: ${(p) => (p.$active ? "#0b5ed7" : "#e9ecef")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* 아이콘 버튼: baseline 아래 여백이 생기지 않도록 */
  svg {
    display: block;
    margin: 0 auto;
  }
`;

const ColorBtn = styled.label`
  position: relative;
  padding: 3px 6px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  min-width: 28px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #495057;
  &:hover { background: #e9ecef; }

  input[type="color"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }
`;

const ColorSwatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 3px;
  border-radius: 1px;
  background: ${(p) => p.$color || "#000"};
`;

const Sep = styled.div`
  width: 1px;
  background: #dee2e6;
  margin: 2px 4px;
`;

const Select = styled.select`
  height: 24px;
  padding: 0 4px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 12px;
  cursor: pointer;
`;

const NumberInput = styled.input`
  width: 52px;
  height: 24px;
  padding: 0 4px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #fff;
  color: #495057;
  font-size: 12px;
`;

const TableBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 5px 8px;
  background: #eef4ff;
  border-bottom: 1px solid #dee2e6;

  .label {
    font-size: 12px;
    font-weight: 600;
    color: #0d6efd;
    margin-right: 4px;
  }
`;

const GroupLabel = styled.span`
  font-size: 11px;
  color: #6c757d;
  margin: 0 2px 0 4px;
`;

const EditorArea = styled.div`
  .ProseMirror {
    padding: ${NARRATIVE_PADDING_Y}px ${NARRATIVE_PADDING_X}px;
    min-height: 200px;
    outline: none;
    font-size: ${NARRATIVE_FONT_SIZE}px;

    /* 뷰어와 공유하는 본문 스타일 (표·체크리스트·첨자 등) */
    ${narrativeContentStyles}

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: #adb5bd;
      pointer-events: none;
      float: left;
      height: 0;
    }

    a { cursor: pointer; }

    ul[data-type="taskList"] li input[type="checkbox"] { cursor: pointer; }

    /* 아래는 편집 중에만 필요한 것들 */
    table {
      td, th { position: relative; }

      .selectedCell::after {
        content: "";
        position: absolute;
        inset: 0;
        background: rgba(13, 110, 253, 0.12);
        pointer-events: none;
      }

      /* 커서가 열 경계 근처에 있을 때만 나타나는 드래그 핸들 */
      .column-resize-handle {
        position: absolute;
        top: 0;
        right: -3px;
        bottom: 0;
        width: 6px;
        z-index: 20;
        background: #0d6efd;
        pointer-events: none;
      }

      /* 행 경계에 커서가 올라갔을 때(끄는 중 포함) 잡히는 경계를 보여 준다.
         열 핸들과 달리 별도 요소가 없으므로 셀 안쪽 그림자로 그린다.
         경계에서 끝나는 셀에만 붙으므로 병합 셀이 있어도 선이 한 높이로 이어진다. */
      td.row-resize-edge,
      th.row-resize-edge {
        box-shadow: inset 0 -3px 0 #0d6efd;
      }
    }

    /* 리사이즈 핸들 위에 커서가 올라가면 prosemirror-tables가 붙이는 클래스 */
    &.resize-cursor {
      cursor: col-resize;
    }

    /* 행 경계 위에서는 행 리사이즈 플러그인이 붙인다 */
    &.row-resize-cursor {
      cursor: row-resize;
    }
  }
`;

const ImportOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ImportBox = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px 28px;
  min-width: 320px;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    color: #495057;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
`;

export default function NarrativeEditor({ value, onChange, readOnly, sfqId }) {
  const prevSfqId = useRef(sfqId);
  const fileInputRef = useRef(null);
  const colorInputRef = useRef(null);
  const backgroundInputRef = useRef(null);
  const cellBgInputRef = useRef(null);
  const cellBorderInputRef = useRef(null);
  const [borderPen, setBorderPen] = useState({
    width: String(CELL_BORDER_WIDTH),
    style: "solid",
    color: CELL_BORDER_COLOR,
  });
  const [showWarning, setShowWarning] = useState(false);
  const [importDialog, setImportDialog] = useState(null);
  // 행 높이 입력칸은 타이핑 중간값("2"만 입력된 상태 등)을 그대로 두어야 하므로
  // 문서 값(active.rowHeight)과 따로 들고 있다가 입력이 끝날 때 반영한다.
  const [rowHeightDraft, setRowHeightDraft] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false, underline: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Image.configure({ inline: true }),
      TextStyle,
      Color,
      FontSize,
      FontFamily,
      // 글자 배경. Word의 글자 음영(w:shd)이 여기로 온다 — 형광펜(w:highlight)은 색이
      // 정해진 몇 가지뿐이라 Highlight가 따로 맡는다.
      BackgroundColor,
      Superscript,
      Subscript,
      Indent,
      TextAlign,
      ListStyle,
      Highlight.configure({ multicolor: true }),
      // 내러티브는 기관으로 나가기만 하는 문서라 체크 상태를 되받을 길이 없다(뷰어도
      // 체크박스를 눌리지 않게 막아 둔다). 그래서 새로 만드는 버튼은 두지 않지만,
      // 이미 저장된 문서나 Word에서 가져온 체크박스는 그대로 그려져야 한다.
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({
        placeholder: "Study background, protocol summary, inclusion/exclusion criteria 등을 입력하세요...",
      }),
      ...tableExtensions,
    ],
    content: value || "",
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (!readOnly && onChange) onChange(editor.getHTML());
    },
  });

  // v3의 useEditor는 트랜잭션마다 리렌더하지 않으므로, 커서/선택 영역의 서식을
  // 툴바에 반영하려면 필요한 값만 골라 구독해야 한다.
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return null;
      const textStyle = e.getAttributes("textStyle");
      const cell = e.isActive("tableHeader")
        ? e.getAttributes("tableHeader")
        : e.getAttributes("tableCell");

      return {
        bold: e.isActive("bold"),
        italic: e.isActive("italic"),
        underline: e.isActive("underline"),
        strike: e.isActive("strike"),
        superscript: e.isActive("superscript"),
        subscript: e.isActive("subscript"),
        heading1: e.isActive("heading", { level: 1 }),
        heading2: e.isActive("heading", { level: 2 }),
        heading3: e.isActive("heading", { level: 3 }),
        bulletList: e.isActive("bulletList"),
        orderedList: e.isActive("orderedList"),
        // 커서가 놓인 목록의 기호 모양 (기본 기호면 null)
        listStyle:
          (e.isActive("bulletList")
            ? e.getAttributes("bulletList").listStyle
            : e.getAttributes("orderedList").listStyle) || null,
        link: e.isActive("link"),
        highlight: e.isActive("highlight"),
        fontSize: textStyle.fontSize || "",
        fontFamily: textStyle.fontFamily || "",
        color: textStyle.color || "",
        background: textStyle.backgroundColor || "",
        // 지정하지 않은 문단은 왼쪽으로 그려지므로 그 값을 활성으로 보여 준다
        textAlign: TEXT_ALIGN_VALUES.find((value) => e.isActive({ textAlign: value })) || "left",
        inTable: e.isActive("table"),
        cellBackground: cell.backgroundColor || "",
        // 지정하지 않은 셀은 스타일시트 기본값이 적용되므로 그 값을 활성으로 보여 준다
        cellAlign: cell.align || "left",
        cellVerticalAlign: cell.verticalAlign || "top",
        // 네 면이 같을 때만 툴바가 그 값을 보여 준다 (섞여 있으면 지정하지 않은 것으로 다룬다)
        cellBorder: uniformBorder(cell),
        // 지정하지 않은 표는 왼쪽에 놓이므로 그 값을 활성으로 보여 준다
        tableAlign: e.getAttributes("table").align || "left",
        // 높이는 셀이 아니라 행에 걸린다. 0이면 지정하지 않은 것(내용에 맞춤).
        rowHeight: e.getAttributes("tableRow").rowHeight || 0,
        canUndo: e.can().undo(),
        canRedo: e.can().redo(),
      };
    },
  });

  useEffect(() => {
    if (editor && sfqId !== prevSfqId.current) {
      prevSfqId.current = sfqId;
      editor.commands.setContent(value || "");
    }
  }, [sfqId, editor, value]);

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  // 테두리를 가진 셀로 커서를 옮기면 펜이 그 값을 집어 든다. 테두리가 없거나 면마다
  // 다른 셀에서는 쓰던 펜을 그대로 둔다(그 셀에 이어서 그릴 값이기 때문).
  const activeCellBorder = active ? active.cellBorder : null;
  useEffect(() => {
    const parsed = parseBorder(activeCellBorder);
    if (parsed && parsed.width) {
      setBorderPen({ width: String(parsed.width), style: parsed.style, color: parsed.color });
    }
  }, [activeCellBorder]);

  // 커서가 다른 행으로 옮겨가거나 경계를 끌어 높이가 바뀌면 입력칸을 그 값으로 맞춘다
  const activeRowHeight = active ? active.rowHeight : 0;
  useEffect(() => {
    setRowHeightDraft(activeRowHeight ? String(activeRowHeight) : "");
  }, [activeRowHeight]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    if (!file.name.toLowerCase().endsWith(".docx")) {
      alert(".docx 형식의 파일만 가져올 수 있습니다.");
      return;
    }

    const html = await convertWordDocument(await file.arrayBuffer(), {
      maxWidth: maxTableWidth(editor),
      minColumnWidth: MIN_COL_WIDTH,
    });
    setImportDialog({ html });
  };

  const applyImport = (mode) => {
    if (mode === "replace") {
      editor.commands.setContent(importDialog.html);
    } else {
      const { from } = editor.state.selection;
      const $pos = editor.state.doc.resolve(from);
      const insertPos = $pos.depth >= 1 ? $pos.after(1) : from;
      editor.commands.insertContentAt(insertPos, importDialog.html);
    }
    if (onChange) onChange(editor.getHTML());
    setImportDialog(null);
  };

  const handleLink = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("URL을 입력하세요:");
      if (url) editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleColorChange = (e) => {
    editor.chain().focus().setColor(e.target.value).run();
  };

  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    if (size) editor.chain().focus().setFontSize(size).run();
    else editor.chain().focus().unsetFontSize().run();
  };

  const handleFontFamilyChange = (e) => {
    const family = e.target.value;
    if (family) editor.chain().focus().setFontFamily(family).run();
    else editor.chain().focus().unsetFontFamily().run();
  };

  const handleBackgroundChange = (e) => {
    editor.chain().focus().setBackgroundColor(e.target.value).run();
  };

  const resetBackground = () => editor.chain().focus().unsetBackgroundColor().run();

  /**
   * 목록 버튼 하나가 종류(•/1.)와 기호 모양(✓/1))을 함께 맡는다.
   *
   * 같은 모양을 다시 누르면 목록을 풀고, 다른 모양이면 항목을 그대로 둔 채 기호만 바꾼다
   * (기호를 바꾸겠다고 목록을 풀었다 다시 만들면 중첩 단계가 무너진다).
   */
  const toggleList = (name, style = null) => {
    const command = name === "bulletList" ? "toggleBulletList" : "toggleOrderedList";
    const chain = editor.chain().focus();

    if (!editor.isActive(name)) {
      chain[command]().updateAttributes(name, { listStyle: style }).run();
      return;
    }

    if ((editor.getAttributes(name).listStyle || null) === style) chain[command]().run();
    else chain.updateAttributes(name, { listStyle: style }).run();
  };

  // 리스트 안에서는 단계 조정, 그 외 문단/헤딩은 margin-left 들여쓰기
  const applyIndent = (dir) => {
    const listCmd = dir > 0 ? "sinkListItem" : "liftListItem";
    const fallback = dir > 0 ? "indent" : "outdent";
    editor
      .chain()
      .focus()
      .command(
        ({ commands }) =>
          commands[listCmd]("listItem") || commands[listCmd]("taskItem") || commands[fallback]()
      )
      .run();
  };

  // 모든 셀의 colwidth를 같은 값으로 지정한다.
  // 열이 하나라도 비면 표가 가변 폭이 되어 열 너비 지정이 무시되므로 항상 전부 채운다.
  const setAllColumnWidths = (width) => {
    const found = findTableAt(editor.state);
    if (!found) return;
    const { node, pos } = found;
    const { tr } = editor.state;

    node.forEach((row, rowOffset) => {
      const rowPos = pos + 1 + rowOffset;
      row.forEach((cell, cellOffset) => {
        const span = cell.attrs.colspan || 1;
        tr.setNodeMarkup(rowPos + 1 + cellOffset, undefined, {
          ...cell.attrs,
          colwidth: new Array(span).fill(width),
        });
      });
    });

    editor.view.dispatch(tr);
    if (onChange) onChange(editor.getHTML());
  };

  // 편집 영역 너비를 열 개수로 나눠 균등 배분
  const distributeColumns = () => {
    const found = findTableAt(editor.state);
    if (!found) return;
    const firstRow = found.node.firstChild;
    if (!firstRow) return;

    let cols = 0;
    firstRow.forEach((cell) => {
      cols += cell.attrs.colspan || 1;
    });
    if (!cols) return;

    setAllColumnWidths(Math.max(MIN_COL_WIDTH, Math.floor(maxTableWidth(editor) / cols)));
  };

  // 선택한 행(셀을 여러 개 골랐으면 걸쳐 있는 행 전부)의 높이를 지정하고, 그중 첫 행에
  // 실제로 들어간 값을 돌려준다. height가 null이면 지정을 지워 내용에 맞춘다.
  //
  // 내용보다 작은 높이는 화면에 반영되지 않으므로 행마다 그 행의 최소 높이로 올려 준다.
  // (지정하지 않은 것과 같은 값이면 아예 비워 둔다 — 쓸데없는 height가 남지 않도록)
  const setRowHeight = (height) => {
    const { tr } = editor.state;
    let changed = false;
    let applied = null;

    selectedRows(editor.state).forEach(({ node, pos }, index) => {
      const natural = naturalRowHeight(editor.view, pos);
      const next = height === null || height <= natural ? null : height;
      if (index === 0) applied = next;

      if ((node.attrs.rowHeight || null) === next) return;
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, rowHeight: next });
      changed = true;
    });

    if (changed) {
      editor.view.dispatch(tr);
      if (onChange) onChange(editor.getHTML());
    }
    editor.commands.focus();
    return applied;
  };

  // 입력칸을 비우면 높이 지정 해제, 숫자면 허용 범위로 맞춰 적용한다.
  // 지정이 무시될 값(내용 높이 이하)을 넣으면 입력칸을 "자동"으로 되돌려, 값은 들어갔는데
  // 화면은 그대로인 상태로 남지 않게 한다.
  const applyRowHeightDraft = () => {
    const value = rowHeightDraft.trim();
    if (!value) {
      setRowHeight(null);
      return;
    }

    const height = Number(value);
    if (!Number.isFinite(height) || height <= 0) {
      setRowHeightDraft(activeRowHeight ? String(activeRowHeight) : "");
      return;
    }

    const applied = setRowHeight(clampRowHeight(height));
    setRowHeightDraft(applied ? String(applied) : "");
  };

  // 표 구조 변경 명령은 새 셀의 너비를 채우는 후처리와 함께 실행한다
  const runTableCommand = (command) => {
    command();
    fillMissingColumnWidths(editor);
  };

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    // 삽입 직후 너비를 고정해 두어야 이후 입력 내용에 따라 열이 흔들리지 않는다
    distributeColumns();
  };

  // 셀 선택(CellSelection) 중이면 선택한 셀 전체에, 아니면 커서가 있는 셀에 적용된다
  const setCellAttribute = (name, value) => {
    editor.chain().focus().setCellAttribute(name, value).run();
  };

  // 셀 안 문단에 정렬이 남아 있으면 그쪽이 셀 정렬을 덮어써 버튼이 먹지 않는 것처럼 보인다.
  // 표 안에서는 문단 정렬을 못 넣게 막아 두었지만, 그 전에 만든 내용이나 Word에서 가져온
  // 표에는 남아 있을 수 있으므로 셀 정렬을 지정할 때 함께 지운다.
  const setCellAlign = (value) => {
    editor
      .chain()
      .focus()
      .setCellAttribute("align", value)
      .command(({ state, tr, dispatch }) => {
        selectedCells(state).forEach(({ node, pos }) => {
          node.descendants((child, offset) => {
            if (child.attrs?.textAlign == null) return;
            // setNodeMarkup은 노드 크기를 바꾸지 않으므로 위치가 어긋나지 않는다
            tr.setNodeMarkup(pos + 1 + offset, undefined, { ...child.attrs, textAlign: null });
          });
        });
        if (dispatch) dispatch(tr);
        return true;
      })
      .run();
  };

  // 테두리 "펜": 어떤 선을 그을지(굵기·색)를 정해 두고, 자리 버튼으로 그 선을 놓는다.
  // 커서가 테두리를 가진 셀로 가면 그 값을 집어 든다(Word의 테두리 그리기와 같은 방식).
  const borderValue = borderPen.width === "0" ? "none" : `${borderPen.width}px ${borderPen.style} ${borderPen.color}`;

  const applyCellBorders = (target, value = borderValue) => {
    editor.chain().focus().setCellBorders(target, value).run();
  };

  const handleCellBgChange = (e) => setCellAttribute("backgroundColor", e.target.value);

  const resetCellBg = () => setCellAttribute("backgroundColor", null);

  if (!editor || !active) return null;

  // Word 임포트 등으로 프리셋에 없는 크기가 들어오면 목록에 임시로 추가한다
  const fontSizeOptions = FONT_SIZES.map((size) => `${size}px`);
  if (active.fontSize && !fontSizeOptions.includes(active.fontSize)) {
    fontSizeOptions.push(active.fontSize);
  }

  // 글꼴도 마찬가지다. Word 문서의 글꼴 조합은 경우의 수가 많아 프리셋으로 다 덮을 수 없다.
  const fontFamilyOptions = FONT_FAMILIES.slice();
  if (active.fontFamily && !fontFamilyOptions.some(([, value]) => value === active.fontFamily)) {
    fontFamilyOptions.push([fontLabel(active.fontFamily), active.fontFamily]);
  }

  const tb = (label, action, isActive) => (
    <Btn $active={isActive} onClick={action} disabled={readOnly} type="button">
      {label}
    </Btn>
  );

  return (
    <Wrapper>
      {!readOnly && (
        <StickyHeader>
          <Toolbar>
            {tb("B", () => editor.chain().focus().toggleBold().run(), active.bold)}
            {tb("I", () => editor.chain().focus().toggleItalic().run(), active.italic)}
            <Btn
              $active={active.underline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              disabled={readOnly}
              type="button"
              style={{ textDecoration: "underline" }}
            >
              U
            </Btn>
            {tb("S", () => editor.chain().focus().toggleStrike().run(), active.strike)}
            <Btn
              $active={active.superscript}
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              disabled={readOnly}
              type="button"
              title="윗첨자 (Ctrl+.)"
            >
              X<sup>2</sup>
            </Btn>
            <Btn
              $active={active.subscript}
              onClick={() => editor.chain().focus().toggleSubscript().run()}
              disabled={readOnly}
              type="button"
              title="아래첨자 (Ctrl+,)"
            >
              X<sub>2</sub>
            </Btn>
            <Sep />
            <Select
              title="글꼴"
              value={active.fontFamily}
              onChange={handleFontFamilyChange}
              disabled={readOnly}
            >
              <option value="">기본 글꼴</option>
              {fontFamilyOptions.map(([label, value]) => (
                <option key={value} value={value} style={{ fontFamily: value }}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              title="글자 크기"
              value={active.fontSize}
              onChange={handleFontSizeChange}
              disabled={readOnly}
            >
              <option value="">기본</option>
              {fontSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
            {tb("H1", () => editor.chain().focus().toggleHeading({ level: 1 }).run(), active.heading1)}
            {tb("H2", () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active.heading2)}
            {tb("H3", () => editor.chain().focus().toggleHeading({ level: 3 }).run(), active.heading3)}
            <Sep />
            <Btn type="button" disabled={readOnly} title="내어쓰기 (Shift+Tab)" onClick={() => applyIndent(-1)}>
              ⇤
            </Btn>
            <Btn type="button" disabled={readOnly} title="들여쓰기 (Tab)" onClick={() => applyIndent(1)}>
              ⇥
            </Btn>
            <Sep />
            {/* 표 안에서 글자 정렬은 표 도구의 셀 정렬이 맡는다(문단 정렬이 셀 정렬을 덮어쓰기
                때문). 그래서 이 버튼들은 표 안에서는 표 자체의 좌우 위치를 맡는다. */}
            {TEXT_ALIGNS.map(([value, icon, title, shortcut]) => {
              const tableTitle = TABLE_ALIGN_TITLES[value];
              return (
                <Btn
                  key={value}
                  type="button"
                  title={
                    active.inTable
                      ? tableTitle
                        ? `${tableTitle} 놓습니다 (열 너비를 지정해 표가 편집 영역보다 좁을 때 보입니다)`
                        : "표는 양쪽 맞춤을 쓰지 않습니다"
                      : `${title} (${shortcut})`
                  }
                  aria-label={active.inTable && tableTitle ? tableTitle : title}
                  disabled={readOnly || (active.inTable && !tableTitle)}
                  $active={active.inTable ? active.tableAlign === value : active.textAlign === value}
                  onClick={() =>
                    active.inTable
                      ? editor.chain().focus().setTableAlign(value === "left" ? null : value).run()
                      : editor.chain().focus().setTextAlign(value).run()
                  }
                >
                  {icon}
                </Btn>
              );
            })}
            <Sep />
            {LIST_BUTTONS.map(([name, style, label, title]) => (
              <Btn
                key={label}
                type="button"
                title={title}
                disabled={readOnly}
                $active={active[name] && active.listStyle === style}
                onClick={() => toggleList(name, style)}
              >
                {label}
              </Btn>
            ))}
            <Sep />
            {tb("—", () => editor.chain().focus().setHorizontalRule().run(), false)}
            <Sep />
            {tb("⊞ Table", insertTable, false)}
            <Sep />
            <ColorBtn
              title={active.color ? `글자 색상 (${active.color})` : "글자 색상"}
              onClick={() => colorInputRef.current?.click()}
            >
              A
              <ColorSwatch $color={active.color || DEFAULT_TEXT_COLOR} />
              <input
                ref={colorInputRef}
                type="color"
                value={toInputColor(active.color, DEFAULT_TEXT_COLOR)}
                onChange={handleColorChange}
              />
            </ColorBtn>
            <ColorBtn
              title={active.background ? `글자 배경색 (${active.background})` : "글자 배경색"}
              onClick={() => backgroundInputRef.current?.click()}
            >
              ▧
              <ColorSwatch $color={active.background || DEFAULT_TEXT_BG} />
              <input
                ref={backgroundInputRef}
                type="color"
                value={toInputColor(active.background, DEFAULT_TEXT_BG)}
                onChange={handleBackgroundChange}
              />
            </ColorBtn>
            <Btn
              type="button"
              title="글자 배경색 지우기"
              disabled={readOnly || !active.background}
              onClick={resetBackground}
            >
              ✕
            </Btn>
            {tb("🖊", () => editor.chain().focus().toggleHighlight({ color: "#fef08a" }).run(), active.highlight)}
            {tb("🔗", handleLink, active.link)}
            <Sep />
            <Btn
              type="button"
              title="실행 취소"
              disabled={readOnly || !active.canUndo}
              onClick={() => editor.chain().focus().undo().run()}
            >
              ↩
            </Btn>
            <Btn
              type="button"
              title="다시 실행"
              disabled={readOnly || !active.canRedo}
              onClick={() => editor.chain().focus().redo().run()}
            >
              ↪
            </Btn>
            <Sep />
            <Btn type="button" disabled={readOnly} onClick={() => setShowWarning(true)}>
              📄 Word
            </Btn>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Toolbar>
          {active.inTable && (
            <TableBar>
              <span className="label">표</span>
              {tb("←열 추가", () => runTableCommand(() => editor.chain().focus().addColumnBefore().run()), false)}
              {tb("열 추가→", () => runTableCommand(() => editor.chain().focus().addColumnAfter().run()), false)}
              {tb("열 삭제", () => runTableCommand(() => editor.chain().focus().deleteColumn().run()), false)}
              <Sep />
              {tb("↑행 추가", () => runTableCommand(() => editor.chain().focus().addRowBefore().run()), false)}
              {tb("행 추가↓", () => runTableCommand(() => editor.chain().focus().addRowAfter().run()), false)}
              {tb("행 삭제", () => runTableCommand(() => editor.chain().focus().deleteRow().run()), false)}
              <Sep />
              {tb("셀 병합", () => runTableCommand(() => editor.chain().focus().mergeCells().run()), false)}
              {tb("셀 분할", () => runTableCommand(() => editor.chain().focus().splitCell().run()), false)}
              <Sep />
              {tb("머리글 행", () => runTableCommand(() => editor.chain().focus().toggleHeaderRow().run()), false)}
              {tb("머리글 열", () => runTableCommand(() => editor.chain().focus().toggleHeaderColumn().run()), false)}
              <Sep />
              <Btn
                type="button"
                title="모든 열을 같은 너비로 맞추고 표를 본문 폭에 채웁니다"
                onClick={distributeColumns}
              >
                ⇹ 열 너비 균등
              </Btn>
              <Sep />
              <GroupLabel>행 높이</GroupLabel>
              <NumberInput
                type="number"
                min={MIN_ROW_HEIGHT}
                max={MAX_ROW_HEIGHT}
                step={5}
                value={rowHeightDraft}
                placeholder="자동"
                title="선택한 행의 높이(px). 내용이 차지하는 높이보다 작게는 줄지 않으며, 그런 값을 넣으면 '자동'으로 되돌아갑니다. 표 안에서 행 경계를 끌어 조절할 수도 있습니다."
                onChange={(e) => setRowHeightDraft(e.target.value)}
                onBlur={applyRowHeightDraft}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  applyRowHeightDraft();
                }}
              />
              <Btn
                type="button"
                title="지정한 행 높이를 지우고 내용에 맞춥니다"
                onClick={() => setRowHeight(null)}
              >
                ⇳ 높이 초기화
              </Btn>
              <Sep />
              <GroupLabel>가로</GroupLabel>
              {CELL_ALIGNS.map(([value, icon, title]) => (
                <Btn
                  key={value}
                  type="button"
                  title={`${title} (선택한 셀 전체)`}
                  aria-label={title}
                  $active={active.cellAlign === value}
                  onClick={() => setCellAlign(value === "left" ? null : value)}
                >
                  {icon}
                </Btn>
              ))}
              <GroupLabel>세로</GroupLabel>
              {CELL_VERTICAL_ALIGNS.map(([value, icon, title]) => (
                <Btn
                  key={value}
                  type="button"
                  title={`${title} (선택한 셀 전체)`}
                  aria-label={title}
                  $active={active.cellVerticalAlign === value}
                  onClick={() => setCellAttribute("verticalAlign", value === "top" ? null : value)}
                >
                  {icon}
                </Btn>
              ))}
              <Sep />
              <ColorBtn
                title={active.cellBackground ? `셀 배경색 (${active.cellBackground})` : "셀 배경색"}
                onClick={() => cellBgInputRef.current?.click()}
              >
                ▦
                <ColorSwatch $color={active.cellBackground || DEFAULT_CELL_BG} />
                <input
                  ref={cellBgInputRef}
                  type="color"
                  value={toInputColor(active.cellBackground, DEFAULT_CELL_BG)}
                  onChange={handleCellBgChange}
                />
              </ColorBtn>
              {tb("✕", resetCellBg, false)}
              <Sep />
              <GroupLabel>테두리</GroupLabel>
              <Select
                value={borderPen.width}
                title="그을 선의 굵기 ('없음'은 선을 지웁니다)"
                onChange={(e) => setBorderPen({ ...borderPen, width: e.target.value })}
              >
                {CELL_BORDER_WIDTHS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <ColorBtn
                title={`그을 선의 색 (${borderPen.color})`}
                onClick={() => cellBorderInputRef.current?.click()}
              >
                ▤
                <ColorSwatch $color={borderPen.color} />
                <input
                  ref={cellBorderInputRef}
                  type="color"
                  value={toInputColor(borderPen.color, CELL_BORDER_COLOR)}
                  onChange={(e) => setBorderPen({ ...borderPen, color: e.target.value })}
                />
              </ColorBtn>
              {BORDER_TARGETS.map(([target, label]) => (
                <Btn
                  key={target}
                  type="button"
                  title={`${label} 테두리 (선택한 셀 전체를 하나로 봅니다)`}
                  aria-label={`${label} 테두리`}
                  onClick={() => applyCellBorders(target)}
                >
                  <BorderIcon anchor={target} />
                </Btn>
              ))}
              <Btn
                type="button"
                title="테두리 지정을 지워 기본 테두리로 되돌립니다"
                aria-label="테두리 지정 지움"
                onClick={() => applyCellBorders("all", null)}
              >
                ✕
              </Btn>
              <Sep />
              {tb("표 삭제", () => editor.chain().focus().deleteTable().run(), false)}
            </TableBar>
            )}
        </StickyHeader>
      )}
      <EditorArea>
        <EditorContent editor={editor} />
      </EditorArea>

      {showWarning && (
        <ImportOverlay>
          <ImportBox>
            <h4>Word 파일 가져오기 안내</h4>
            <ul>
              <li>.docx 형식만 지원합니다 (.doc 구형 포맷 불가)</li>
              <li>복잡한 서식(커스텀 테마, 텍스트박스 등)은 변환 시 손실될 수 있습니다</li>
              <li>이미지는 base64로 변환되며, 용량이 큰 경우 성능 저하가 발생할 수 있습니다</li>
            </ul>
            <div className="actions">
              <Btn
                type="button"
                onClick={() => {
                  setShowWarning(false);
                  fileInputRef.current?.click();
                }}
              >
                확인
              </Btn>
              <Btn type="button" onClick={() => setShowWarning(false)}>
                취소
              </Btn>
            </div>
          </ImportBox>
        </ImportOverlay>
      )}

      {importDialog && (
        <ImportOverlay>
          <ImportBox>
            <p>Word 파일을 어떻게 가져올까요?</p>
            <div className="actions">
              <Btn type="button" onClick={() => applyImport("replace")}>
                기존 내용 교체
              </Btn>
              <Btn type="button" onClick={() => applyImport("insert")}>
                커서 위치에 삽입
              </Btn>
              <Btn type="button" onClick={() => setImportDialog(null)}>
                취소
              </Btn>
            </div>
          </ImportBox>
        </ImportOverlay>
      )}
    </Wrapper>
  );
}
